<script lang="ts" setup>
import type { Component } from 'vue'

import AbstractButton from '../abstract-button/AbstractButton.vue'
import ActivityIndicator from '../activity-indicator/ActivityIndicator.vue'
import Icon from '../icon/Icon.vue'
import type { TIconName } from '../icon/types'
import {
  TextButtonIconPosition,
  TextButtonSize,
  TextButtonVariant,
} from './helpers'
import type {
  TTextButtonIconPosition,
  TTextButtonSize,
  TTextButtonVariant,
} from './types'

withDefaults(
  defineProps<{
    /**
     * The HTML tagname to use when rendering the component. Defaults to `button`.
     */
    // Note: this shadows the `AbstractButton` prop. Required for Options API
    as?: keyof HTMLElementTagNameMap | Component
    /**
     * The theme variant to use for styling the button
     */
    // Note: this shadows the `AbstractButton` prop. Required for Options API
    variant?: TTextButtonVariant
    /**
     * Indicates whether the user should not be able to interact with the input.
     */
    // Note: this shadows the `AbstractButton` prop. Required for Options API
    disabled?: boolean
    /**
     * Optional icon to show alongside the text content. The icon can be
     * positioned using the `icon-position` property.
     */
    icon?: TIconName
    /**
     * Controls the size of the button
     */
    size?: TTextButtonSize
    /**
     * Determines where the optional icon will appear.
     */
    iconPosition?: TTextButtonIconPosition
    /**
     * A text string that is used as the button's label
     */
    label: string
    /**
     * Sets the button in a busy state to indicate an asynchrous action has
     * been triggered and that the button is unavailabe unti it completes.
     */
    busy?: boolean
  }>(),
  {
    as: 'button',
    variant: TextButtonVariant.Primary,
    disabled: false,
    busy: false,
    size: TextButtonSize.Medium,
    iconPosition: TextButtonIconPosition.End,
  }
)
</script>

<template>
  <AbstractButton
    v-bind="$attrs"
    :aria-label="busy ? 'Processing' : ''"
    :aria-disabled="busy"
    :as
    :variant
    :disabled
    class="glp-text-button"
    :class="{
      'glp-text-button--sm': size === TextButtonSize.Small,
      'glp-text-button--lg': size === TextButtonSize.Large,
      'glp-text-button--busy': busy,
      'glp-text-button--icon-start':
        iconPosition === TextButtonIconPosition.Start,
    }"
  >
    {{ label }}
    <Icon v-if="icon" :name="icon" />
    <ActivityIndicator v-if="busy" class="busy-indicator" />
  </AbstractButton>
</template>

<style>
.glp-text-button {
  white-space: nowrap;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-family: inter;
  font-style: normal;
  padding: calc(
      var(--glp-button-padding-vertical, 8px) -
        var(--glp-button-border-width, 0px)
    )
    calc(
      var(--glp-button-padding-horizontal, 16px) -
        var(--glp-button-border-width, 0px)
    );
  gap: var(--glp-button-text-md-gap, 8px);
  font-size: var(--glp-button-font-size);
  font-weight: var(--glp-button-font-weight);
  line-height: var(--glp-button-icon-size);
  --glp-button-font-weight: var(--glp-button-text-font-weight, 700);
  --glp-button-font-size: var(--glp-button-text-md-font-size, 14px);
  --glp-button-icon-size: var(--glp-button-text-md-size-icon, 16px);
  --glp-button-padding-horizontal: var(
    --glp-button-text-md-padding-horizontal,
    16px
  );
  --glp-button-padding-vertical: var(
    --glp-button-text-md-padding-vertical,
    8px
  );
}

.glp-text-button--icon-start .glp-icon {
  order: -1;
}

.glp-text-button .glp-icon {
  --glp-icon-size: var(--glp-button-icon-size);
  flex-shrink: 0;
}

.glp-text-button--lg {
  --glp-button-padding-horizontal: var(
    --glp-button-text-lg-padding-horizontal,
    24px
  );
  --glp-button-padding-vertical: var(
    --glp-button-text-lg-padding-vertical,
    12px
  );
  --glp-button-icon-size: var(--glp-button-text-lg-size-icon, 16px);
  gap: var(--glp-button-text-lg-gap, 8px);
  --glp-button-font-size: var(--glp-button-text-lg-font-size, 16px);
}

.glp-text-button--sm {
  --glp-button-padding-horizontal: var(
    --glp-button-text-sm-padding-horizontal,
    8px
  );
  --glp-button-padding-vertical: var(
    --glp-button-text-sm-padding-vertical,
    6px
  );
  --glp-button-icon-size: var(--glp-button-text-sm-size-icon, 12px);
  gap: var(--glp-button-text-sm-gap, 4px);
  --glp-button-font-size: var(--glp-button-text-sm-font-size, 12px);
}

.glp-text-button--busy {
  /** Hide any content by making it transparent */
  color: transparent;
  /** Disable pointer interaction */
  pointer-events: none;
}

.busy-indicator {
  position: absolute;
  color: var(--glp-button-foreground);
  width: 1.2em;
}
</style>
